import { Box, Button, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { Pill } from '../../../shared/components/Pill';
import { SEMANTIC_COLOR_VARS } from '../../../shared/components/semanticColor';
import { formatRelativeTime } from '../../../shared/components/relativeTime';
import type { EntityTypeEnrichment, EntityTypeSkill } from '../../../core/types/data-models';

/**
 * One entity type skill on the Main tab. An ENTITY_TYPE-scope skill (the
 * original behaviour) shows when it last ran (its EntityTypeEnrichment
 * topic, if any) and a "View" link onto the topic's tab. An ENTITY-scope
 * skill researches one entity the caller picks each run — see
 * MainTab's entity-picker dialog — so there is no single topic to show or
 * view here; instead the card is flagged "Per entity" and shows how many of
 * the type's entities have a topic for this skill so far. Its results are
 * viewed on that entity's own card and detail panel instead.
 */
export function SkillCard({
  skill,
  topic,
  entityRunCount,
  totalEntityCount,
  running,
  disabled,
  canRun,
  onRun,
  onView,
}: {
  skill: EntityTypeSkill;
  /** Only meaningful for an ENTITY_TYPE-scope skill. */
  topic: EntityTypeEnrichment | undefined;
  /** ENTITY-scope skills only: how many managed entities already have a topic from this skill. */
  entityRunCount?: number;
  /** ENTITY-scope skills only: managed entities of the type, for the "X of Y" caption. */
  totalEntityCount?: number;
  /** This skill's own run is in flight. */
  running: boolean;
  /** Any project job is in flight — only one can run at a time. */
  disabled: boolean;
  canRun: boolean;
  onRun: () => void;
  onView: () => void;
}) {
  const isEntityScoped = skill.scope === 'ENTITY';
  const hasRun = isEntityScoped ? (entityRunCount ?? 0) > 0 : !!topic;

  return (
    <GlassPanel
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: 0,
        // A run already exists: tint the card green so it reads as "content
        // available" at a glance. `.glass-panel.MuiPaper-root` paints the
        // surface at two-class specificity, so the override needs three.
        ...(hasRun
          ? {
              '&&.MuiPaper-root': {
                bgcolor: SEMANTIC_COLOR_VARS.success.soft,
                borderColor: SEMANTIC_COLOR_VARS.success.line,
              },
            }
          : {}),
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} useFlexGap flexWrap="wrap">
          <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
            {skill.name}
          </Typography>
          {skill.description && (
            <Tooltip
              title={skill.description}
              arrow
              placement="top"
              // The default tooltip type is 11px, too small for a paragraph of
              // prose — same readable treatment DocumentDetail gives a
              // section's AI summary, at the app's base body size.
              slotProps={{ tooltip: { sx: { fontSize: 'var(--font-size-base)', lineHeight: 1.6, maxWidth: 380, p: 1.25 } } }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help', flexShrink: 0 }} />
            </Tooltip>
          )}
          {isEntityScoped && (
            <Tooltip title="Researches one entity you pick each time, not every entity of the type" arrow placement="top">
              <Box sx={{ display: 'inline-flex' }}>
                <Pill color="secondary" mono={false}>
                  <PersonSearchOutlinedIcon sx={{ fontSize: 12 }} />
                  Per entity
                </Pill>
              </Box>
            </Tooltip>
          )}
        </Stack>
        <Typography variant="caption" color={hasRun ? 'text.secondary' : 'text.disabled'}>
          {running
            ? 'Running…'
            : isEntityScoped
              ? `${entityRunCount ?? 0} of ${totalEntityCount ?? 0} managed entities analyzed`
              : topic
                ? `Last run ${formatRelativeTime(topic.updatedAt)}`
                : 'Not run yet'}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 'auto' }}>
        {canRun && (
          <Button
            size="small"
            variant="outlined"
            startIcon={running ? <CircularProgress size={14} /> : <AutorenewIcon />}
            onClick={onRun}
            disabled={disabled}
          >
            {isEntityScoped ? 'Run…' : topic ? 'Re-run' : 'Run'}
          </Button>
        )}
        {!isEntityScoped && topic && (
          <Button size="small" variant="text" endIcon={<ArrowForwardIcon />} onClick={onView}>
            View
          </Button>
        )}
      </Stack>
    </GlassPanel>
  );
}
